import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { ChevronLeft, Menu } from 'lucide-react'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { SidebarContent } from '@/components/Sidebar'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { learningApi } from '@/utils/api'
import { resolveBackendUrl, isExternalVideo, getEmbedUrl } from '@/utils/media'
import { useExternalVideoProgress } from '@/hooks/useExternalVideoProgress'
import { toast } from 'sonner'

const PROGRESS_INTERVAL_MS = 7_000 // send progress every 7 seconds

export default function VideoPlayerPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { video, moduleTitle, moduleId, moduleThumbnail } = location.state || {}
  const [reflection, setReflection] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [completed, setCompleted] = useState(false)
  const [watchedPct, setWatchedPct] = useState(0)
  const [watchedTime, setWatchedTime] = useState(0)
  const [totalTime, setTotalTime] = useState(0)
  const { user } = useAuth()
  const displayName = getUserDisplayName(user)
  const initials = getUserInitials(user)
  const coupleId = (user as { coupleId?: string })?.coupleId
  const videoSrc = resolveBackendUrl(video?.videoUrl)
  const posterSrc = resolveBackendUrl(moduleThumbnail)
  const videoRef = useRef<HTMLVideoElement>(null)
  const progressInterval = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastSentTime = useRef(0)
  const externalLastSentTime = useRef(0)

  const isExternal = isExternalVideo(videoSrc)

  // External video progress callback
  const handleExternalProgress = useCallback(async (ws: number, td: number) => {
    if (!video?.id || !coupleId || completed || td <= 0 || ws === externalLastSentTime.current) return
    externalLastSentTime.current = ws
    try {
      const res = await learningApi.updateVideoProgress(video.id, coupleId, { watchedSeconds: ws, totalDuration: td })
      const data = (res as { data?: { isCompleted?: boolean } }).data
        || (res as unknown as { isCompleted?: boolean })
      if (data?.isCompleted) setCompleted(true)
    } catch { /* retry next interval */ }
  }, [video?.id, coupleId, completed])

  const {
    currentTime: extCurrentTime,
    duration: extDuration,
    percentage: extPercentage,
    iframeRef: extIframeRef,
    seekTo: extSeekTo,
  } = useExternalVideoProgress({
    videoUrl: isExternal ? videoSrc : null,
    onProgress: handleExternalProgress,
    disabled: completed,
  })

  // Keep watchedPct/watchedTime/totalTime in sync for external videos
  useEffect(() => {
    if (!isExternal) return
    setWatchedTime(extCurrentTime)
    setTotalTime(extDuration)
    setWatchedPct(extPercentage)
  }, [isExternal, extCurrentTime, extDuration, extPercentage])

  // Start module on mount
  useEffect(() => {
    const start = async () => {
      if (!moduleId || !coupleId) return
      try {
        await learningApi.startModule(moduleId, coupleId)
      } catch {
        // ignore
      }
    }
    void start()
  }, [moduleId, coupleId])

  // Load saved progress for resume playback
  useEffect(() => {
    const loadProgress = async () => {
      if (!video?.id || !coupleId) return
      try {
        const res = await learningApi.getVideoProgress(video.id, coupleId)
        const data = (res as { data?: { watchedSeconds?: number; isCompleted?: boolean } }).data
          || (res as unknown as { watchedSeconds?: number; isCompleted?: boolean })
        if (data?.isCompleted) {
          setCompleted(true)
        }
        if (data?.watchedSeconds && data.watchedSeconds > 0) {
          if (isExternal) {
            // Seek external player once it's ready
            extSeekTo(data.watchedSeconds)
          } else if (videoRef.current) {
            videoRef.current.currentTime = data.watchedSeconds
          }
          setWatchedTime(data.watchedSeconds)
        }
      } catch {
        // No prior progress — start from beginning
      }
    }
    void loadProgress()
  }, [video?.id, coupleId, isExternal, extSeekTo])

  // Send progress to backend
  const sendProgress = useCallback(async () => {
    const vid = videoRef.current
    if (!vid || !video?.id || !coupleId || completed) return
    const watchedSeconds = Math.floor(vid.currentTime)
    const totalDuration = Math.floor(vid.duration) || 0
    if (totalDuration <= 0 || watchedSeconds === lastSentTime.current) return
    lastSentTime.current = watchedSeconds
    try {
      const res = await learningApi.updateVideoProgress(video.id, coupleId, { watchedSeconds, totalDuration })
      const data = (res as { data?: { isCompleted?: boolean } }).data
        || (res as unknown as { isCompleted?: boolean })
      if (data?.isCompleted) {
        setCompleted(true)
      }
    } catch {
      // Silently fail — will retry on next interval
    }
  }, [video?.id, coupleId, completed])

  // Auto-track progress during playback
  useEffect(() => {
    const vid = videoRef.current
    if (!vid) return

    const onPlay = () => {
      if (progressInterval.current) clearInterval(progressInterval.current)
      progressInterval.current = setInterval(sendProgress, PROGRESS_INTERVAL_MS)
    }
    const onPause = () => {
      sendProgress() // send final position on pause
      if (progressInterval.current) { clearInterval(progressInterval.current); progressInterval.current = null }
    }
    const onEnded = () => {
      sendProgress()
      if (progressInterval.current) { clearInterval(progressInterval.current); progressInterval.current = null }
    }
    const onTimeUpdate = () => {
      if (!vid.duration) return
      setWatchedTime(Math.floor(vid.currentTime))
      setTotalTime(Math.floor(vid.duration))
      setWatchedPct((vid.currentTime / vid.duration) * 100)
    }

    vid.addEventListener('play', onPlay)
    vid.addEventListener('pause', onPause)
    vid.addEventListener('ended', onEnded)
    vid.addEventListener('timeupdate', onTimeUpdate)

    return () => {
      vid.removeEventListener('play', onPlay)
      vid.removeEventListener('pause', onPause)
      vid.removeEventListener('ended', onEnded)
      vid.removeEventListener('timeupdate', onTimeUpdate)
      if (progressInterval.current) clearInterval(progressInterval.current)
    }
  }, [sendProgress])

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  if (!video) {
    navigate('/learning-path')
    return null
  }

  const handleReflection = async () => {
    if (!video?.id || !reflection.trim()) {
      setError('Enter a reflection before submitting.')
      return
    }
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      await learningApi.submitReflection(video.id, reflection.trim())
      setMessage('Reflection submitted.')
    } catch (err) {
      setError((err as Error)?.message || 'Unable to submit reflection.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='flex h-screen overflow-hidden bg-[#F8F9FA]'>
      {/* Main Content */}
      <div className='flex-1 flex flex-col overflow-hidden'>
        {/* Header with mobile menu */}
        <header className='bg-white border-b border-gray-200 px-4 md:px-6 lg:px-8 py-4 shrink-0'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              {/* Mobile Menu Button */}
              <Sheet>
                <SheetTrigger asChild>
                  <button className='lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors'>
                    <Menu className='h-6 w-6 text-gray-700' />
                  </button>
                </SheetTrigger>
                  <SheetContent side='left' className='p-0 w-[280px]'>
                    <SidebarContent organizationName={user?.coupleOrganizationName || 'IRIS Dashboard'} />
                  </SheetContent>
              </Sheet>

              <h1 className='text-xl md:text-2xl font-bold text-gray-900'>Learning Path</h1>
            </div>

            {/* Header Right Section */}
            <div className='flex items-center gap-3 md:gap-4'>
              {/* User Profile */}
              <div className='flex items-center gap-2 md:gap-3'>
                <div className='h-9 w-9 md:h-10 md:w-10 rounded-full bg-gray-800 flex items-center justify-center text-white font-semibold text-sm md:text-base'>
                  {initials}
                </div>
                <span className='hidden md:block font-semibold text-gray-900'>{displayName}</span>
                <button className='hidden md:block'>
                  <svg
                    className='h-4 w-4 text-gray-600'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z'
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Scrollable Content */}
        <main className='flex-1 overflow-y-auto'>
          <div className='p-4 md:p-6 lg:p-8'>
            <div className='mx-auto'>
              {/* Breadcrumb */}
              <button
                onClick={() => navigate('/learning-path')}
                className='flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 transition-colors'
              >
                <ChevronLeft className='h-4 w-4' />
                <span>
                  {moduleTitle}: <span className='font-semibold'>{video.title}</span>
                </span>
              </button>

              {/* Description */}
              <p className='text-sm text-gray-600 mb-6'>
                This module explains why equality and mutual respect form the foundation of a
                peaceful marriage. You'll learn how both partners can feel valued, how small daily
                actions reflect respect, and why unequal treatment can slowly weaken trust. The
                video gives examples of shared decision-making and shows how respect strengthens
                love and teamwork.
              </p>

              {/* Video Player */}
              <div className='bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-200 mb-8'>
                <div className='relative aspect-video bg-gradient-to-br from-orange-200 via-amber-200 to-orange-300'>
                  {videoSrc && isExternalVideo(videoSrc) ? (
                    <iframe
                      ref={extIframeRef}
                      className='absolute inset-0 h-full w-full'
                      src={getEmbedUrl(videoSrc) || ''}
                      allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                      allowFullScreen
                      frameBorder='0'
                    />
                  ) : videoSrc ? (
                    <video
                      ref={videoRef}
                      className='absolute inset-0 h-full w-full object-cover'
                      controls
                      poster={posterSrc || undefined}
                      src={videoSrc}
                    />
                  ) : (
                    <>
                      {/* Placeholder for video player - replace with actual video component */}
                      <div className='absolute inset-0 flex items-center justify-center'>
                        <div className='h-16 w-16 rounded-full bg-white flex items-center justify-center shadow-lg'>
                          <svg
                            className='h-8 w-8 text-gray-900 ml-1'
                            fill='currentColor'
                            viewBox='0 0 20 20'
                          >
                            <path d='M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z' />
                          </svg>
                        </div>
                      </div>

                      {/* Video Controls */}
                      <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4'>
                        <div className='flex items-center gap-4 text-white'>
                          <button className='hover:scale-110 transition-transform'>
                            <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
                              <path
                                fillRule='evenodd'
                                d='M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z'
                                clipRule='evenodd'
                              />
                            </svg>
                          </button>
                          <button className='hover:scale-110 transition-transform'>
                            <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
                              <path d='M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z' />
                              <path d='M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z' />
                            </svg>
                          </button>
                          <span className='text-sm font-semibold'>22:38</span>
                          <div className='flex-1 h-1 bg-white/30 rounded-full mx-4'>
                            <div className='h-full w-1/3 bg-orange-500 rounded-full' />
                          </div>
                          <button className='text-xs font-semibold bg-white/20 px-2 py-1 rounded'>
                            CC
                          </button>
                          <button className='hover:scale-110 transition-transform'>
                            <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
                              <path
                                fillRule='evenodd'
                                d='M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 6a1 1 0 011-1h12a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6z'
                                clipRule='evenodd'
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Watch Progress Indicator */}
              {videoSrc && (
                <div className='bg-white rounded-2xl p-4 border border-gray-200 mb-4'>
                  <div className='flex items-center justify-between text-sm text-gray-600 mb-2'>
                    <span className='font-medium'>
                      {completed ? 'Completed' : `${Math.round(watchedPct)}% watched`}
                    </span>
                    <div className='flex items-center gap-3'>
                      <span className='font-medium text-sm text-gray-600'>
                        {formatTime(watchedTime)} / {formatTime(totalTime)}
                      </span>
                      {!completed && moduleId && video?.id && coupleId && (
                        <button
                          className='px-3 py-1.5 text-xs font-semibold text-white bg-[#FA6017] hover:bg-[#e55109] rounded-lg transition-colors'
                          onClick={async () => {
                            try {
                              await learningApi.completeVideo(moduleId, video.id, coupleId)
                              setCompleted(true)
                              toast.success('Video marked as complete!')
                            } catch {
                              toast.error('Failed to mark video as complete.')
                            }
                          }}
                        >
                          Mark as Complete
                        </button>
                      )}
                    </div>
                  </div>
                  <div className='h-2 w-full overflow-hidden rounded-full bg-gray-200'>
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${
                        completed ? 'bg-green-500' : 'bg-[#FA6017]'
                      }`}
                      style={{ width: `${completed ? 100 : watchedPct}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Reflection Section */}
              <div className='bg-white rounded-2xl p-6 md:p-8 border border-gray-200'>
                <div className='flex items-center justify-between'>
                  <div>
                    <h3 className='text-xl font-bold text-gray-900 mb-2'>Reflection</h3>
                    <p className='text-sm text-gray-600 mb-1'>
                      Share what resonated with you. This stays private to you and your counselor.
                    </p>
                  </div>
                  {completed && (
                    <div className='flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 border border-green-200'>
                      <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                        <circle cx='12' cy='12' r='12' fill='#0ECF5B' />
                        <path d='M8 12l3 3 5-6' stroke='white' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                      </svg>
                      <span className='text-sm font-semibold text-green-700'>Completed</span>
                    </div>
                  )}
                </div>
                <textarea
                  value={reflection}
                  onChange={(e) => setReflection(e.target.value)}
                  placeholder='Submit Your Thoughts'
                  className='w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none text-sm'
                />
                {(error || message) && (
                  <div
                    className={`mt-3 text-sm rounded-lg px-4 py-3 ${
                      error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                    }`}
                  >
                    {error || message}
                  </div>
                )}
                <div className='flex justify-end mt-3'>
                  <button
                    onClick={handleReflection}
                    disabled={saving}
                    className='px-4 py-2 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-60'
                  >
                    {saving ? 'Submitting...' : 'Submit Reflection'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
